import { useEffect, useState } from "react";
import { animate } from "framer-motion";
import { Calculator, MapPin, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { Reveal, Kicker } from "./Reveal";
import { scrollToSection } from "../../lib/scroll";

const SERVICES = [
  { key: "Loft Conversion", min: 38000, max: 65000 },
  { key: "Extension", min: 45000, max: 85000 },
  { key: "Kitchen Renovation", min: 10000, max: 25000 },
  { key: "Bathroom Renovation", min: 6000, max: 14000 },
  { key: "Roofing", min: 6000, max: 14000 },
  { key: "Electrical", min: 2500, max: 9000 },
  { key: "Plumbing", min: 1500, max: 7000 },
  { key: "Full Renovation", min: 60000, max: 140000 },
];

const SIZES = [
  { label: "Small", mult: 0.7 },
  { label: "Medium", mult: 1 },
  { label: "Large", mult: 1.35 },
];

const SERVED_AREAS = ["TW", "UB", "HA", "W", "SW", "NW", "KT", "SL", "WD", "GU"];

const GBP = (v) => `£${Math.round(v).toLocaleString("en-GB")}`;

const inputCls =
  "w-full rounded-none border border-black/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-black/30 transition-colors duration-300 focus:border-bronze focus:outline-none focus:ring-1 focus:ring-bronze";

const Estimator = () => {
  const [service, setService] = useState(SERVICES[0].key);
  const [size, setSize] = useState("Medium");
  const [range, setRange] = useState({ low: 0, high: 0 });

  const current = SERVICES.find((s) => s.key === service);
  const mult = SIZES.find((s) => s.label === size).mult;
  const target = { low: current.min * mult, high: current.max * mult };

  useEffect(() => {
    const controlsLow = animate(range.low, target.low, {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setRange((r) => ({ ...r, low: v })),
    });
    const controlsHigh = animate(range.high, target.high, {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setRange((r) => ({ ...r, high: v })),
    });
    return () => {
      controlsLow.stop();
      controlsHigh.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, size]);

  const exactQuote = () => {
    window.dispatchEvent(new CustomEvent("prefill-service", { detail: service }));
    scrollToSection("#contact");
  };

  return (
    <div className="flex h-full flex-col border border-black/10 bg-white p-8 md:p-10">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center border border-bronze/40">
          <Calculator className="h-5 w-5 text-bronze" strokeWidth={1.8} />
        </span>
        <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-ink">
          Instant Estimate
        </h3>
      </div>

      <label className="mb-2 mt-8 block text-[11px] font-medium uppercase tracking-[0.25em] text-black/50">
        Service
      </label>
      <select
        data-testid="estimate-service-select"
        value={service}
        onChange={(e) => setService(e.target.value)}
        className={`${inputCls} appearance-none`}
      >
        {SERVICES.map((s) => (
          <option key={s.key} value={s.key} className="bg-white text-ink">
            {s.key}
          </option>
        ))}
      </select>

      <label className="mb-2 mt-6 block text-[11px] font-medium uppercase tracking-[0.25em] text-black/50">
        Project Size
      </label>
      <div className="grid grid-cols-3 gap-px border border-black/10 bg-black/10">
        {SIZES.map((s) => (
          <button
            key={s.label}
            data-testid={`estimate-size-${s.label.toLowerCase()}`}
            onClick={() => setSize(s.label)}
            className={`py-3 text-xs font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
              size === s.label ? "bg-ink text-bronze" : "bg-white text-ink hover:bg-steel"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="mt-8 flex-1 border border-bronze/30 bg-coal p-6">
        <div className="text-[11px] uppercase tracking-[0.25em] text-black/50">
          Typical range — {service}, {size.toLowerCase()}
        </div>
        <div data-testid="estimate-result" className="mt-3 font-display text-4xl font-extrabold tracking-tight text-ink md:text-5xl">
          {GBP(range.low)} <span className="text-bronze">–</span> {GBP(range.high)}
        </div>
        <p className="mt-3 text-xs leading-relaxed text-fog">
          Indicative only, based on recent {service.toLowerCase()} projects. Every
          job gets a free, itemised written quote after a site survey.
        </p>
      </div>

      <button
        data-testid="estimate-cta-btn"
        onClick={exactQuote}
        className="mt-6 flex w-full items-center justify-center gap-3 bg-bronze px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-ink hover:text-bronze"
      >
        Get my exact quote <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
};

const PostcodeChecker = () => {
  const [postcode, setPostcode] = useState("");
  const [result, setResult] = useState(null);

  const check = (e) => {
    e.preventDefault();
    const cleaned = postcode.trim().toUpperCase();
    if (!/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(cleaned)) {
      setResult({ ok: null, message: "Enter a valid UK postcode, e.g. TW3 1SF." });
      return;
    }
    const area = cleaned.match(/^([A-Z]{1,2})/)[1];
    if (SERVED_AREAS.includes(area)) {
      setResult({
        ok: true,
        message: `Good news — we cover ${area}. Book your free site survey today.`,
      });
    } else {
      setResult({
        ok: false,
        message: `${area} is outside our core area — get in touch anyway, we may still be able to help.`,
      });
    }
  };

  return (
    <div className="border border-black/10 bg-white p-8 md:p-10">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center border border-bronze/40">
          <MapPin className="h-5 w-5 text-bronze" strokeWidth={1.8} />
        </span>
        <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-ink">
          Do We Cover You?
        </h3>
      </div>
      <form onSubmit={check} className="mt-8 flex gap-3">
        <input
          data-testid="postcode-input"
          type="text"
          value={postcode}
          onChange={(e) => {
            setPostcode(e.target.value);
            setResult(null);
          }}
          placeholder="Enter your postcode"
          className={`${inputCls} uppercase`}
        />
        <button
          data-testid="postcode-check-btn"
          type="submit"
          className="shrink-0 bg-ink px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-bronze transition-colors duration-300 hover:bg-black"
        >
          Check
        </button>
      </form>
      {result && (
        <div
          data-testid="postcode-result"
          className={`mt-4 flex items-start gap-3 border px-4 py-3.5 text-sm ${
            result.ok === null
              ? "border-black/15 bg-coal text-ink/80"
              : result.ok
                ? "border-bronze/40 bg-bronze/10 text-ink"
                : "border-black/15 bg-coal text-ink/80"
          }`}
        >
          {result.ok === true && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-bronze" />}
          {result.ok === false && <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-black/40" />}
          {result.ok === null && <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-black/40" />}
          {result.message}
        </div>
      )}
      <div className="mt-6 flex flex-wrap gap-2">
        {SERVED_AREAS.map((area) => (
          <span
            key={area}
            className="border border-black/10 bg-coal px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-black/60"
          >
            {area}
          </span>
        ))}
      </div>
    </div>
  );
};

export const Estimate = () => (
  <section
    id="estimate"
    data-testid="estimate-section"
    className="border-y border-black/5 bg-coal py-24 md:py-36"
  >
    <div className="mx-auto max-w-7xl px-6">
      <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
        <div>
          <Reveal>
            <Kicker index="07" label="Estimates & Coverage" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight text-ink md:text-6xl">
              Price It.
              <span className="block text-gold-shine">Check We Cover You.</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <p className="max-w-sm text-sm leading-relaxed text-fog">
            Based at 18 Alderwick Drive, Hounslow — covering West and South West
            London, Middlesex and the Surrey borders.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid gap-6 lg:grid-cols-2">
        <Reveal className="h-full">
          <Estimator />
        </Reveal>
        <div className="flex flex-col gap-6">
          <Reveal delay={0.1}>
            <PostcodeChecker />
          </Reveal>
          <Reveal delay={0.18} className="flex-1">
            <div className="relative h-full min-h-[280px] overflow-hidden border border-black/10">
              <iframe
                data-testid="coverage-map"
                title="Sunnys Construction coverage map — Hounslow, West London"
                src="https://www.openstreetmap.org/export/embed.html?bbox=-0.62%2C51.36%2C-0.10%2C51.58&layer=mapnik&marker=51.4686%2C-0.3617"
                className="absolute inset-0 h-full w-full grayscale-[0.35] contrast-[1.05]"
                loading="lazy"
              />
              <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-white/90 px-3 py-2 backdrop-blur-md">
                <span className="h-2 w-2 bg-bronze" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-ink/85">
                  Head Office — Hounslow TW3
                </span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
